import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MoneyInput } from '../ui/money-input';
import { AtLimit } from '../ui/char-counter';
import CalendarModal from '../CalendarModal';
import { X, Calendar, DollarSign, CreditCard, TrendingUp, AlertTriangle } from 'lucide-react';
import { formatMoney, parseMoney } from '../../utils/format';
import { showWarning } from '../Alerts';
import { PaymentMethod } from '../../services/saleService';
import { cacIndexService } from '../../services/cacIndexService';


interface PaymentMethodModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onAddPayment: (payment: PaymentMethod) => void;
    onUpdatePayment: (payment: PaymentMethod) => void;
    editingMethod: PaymentMethod | null;
    totalAmount: number;
    baseCurrency: 'ARS' | 'USD'; // Currency of the sale
    currentAmount: number;
    exchangeRate: string;
    onExchangeRateChange: (rate: string) => void;
}

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
    open,
    onOpenChange,
    onAddPayment,
    onUpdatePayment,
    editingMethod,
    totalAmount,
    baseCurrency,
    currentAmount,
    exchangeRate,
    onExchangeRateChange
}) => {
    // Form state
    const [currency, setCurrency] = useState<'ARS' | 'USD'>('ARS');
    const [paymentType, setPaymentType] = useState<'cash' | 'financing'>('cash');
    const [accountingType, setAccountingType] = useState<'A' | 'B'>('A');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [iva, setIva] = useState('');
    const [paymentDate, setPaymentDate] = useState('');

    // Financing fields
    const [financingType, setFinancingType] = useState('');
    const [firstInstallmentDate, setFirstInstallmentDate] = useState('');
    const [installments, setInstallments] = useState<number>(1);
    const [updateIndex, setUpdateIndex] = useState('');
    const [indexBaseDate, setIndexBaseDate] = useState('');
    const [moraRate, setMoraRate] = useState('');
    const [moraFrequency, setMoraFrequency] = useState('');

    // Calendar modals
    const [isPaymentDateCalendarOpen, setIsPaymentDateCalendarOpen] = useState(false);
    const [isFirstInstallmentCalendarOpen, setIsFirstInstallmentCalendarOpen] = useState(false);
    const [isIndexBaseDateCalendarOpen, setIsIndexBaseDateCalendarOpen] = useState(false);

    // Warning cuando la fecha base CAC es posterior al último período disponible
    const [cacWarning, setCacWarning] = useState<string | null>(null);

    // Calculate progress
    // Convert current input amount to base currency for live progress
    const convertToBaseCurrency = (amt: number, curr: 'ARS' | 'USD', methodExchangeRate?: string): number => {
        const rate = parseFloat(methodExchangeRate || exchangeRate) || 1;

        // If payment currency matches base currency, no conversion needed
        if (curr === baseCurrency) {
            return amt;
        }

        // Convert based on base currency
        if (baseCurrency === 'USD') {
            // Converting ARS to USD
            return amt / rate;
        } else {
            // Converting USD to ARS
            return amt * rate;
        }
    };

    // Parse current input amount and convert to base currency
    const currentInputAmount = parseMoney(amount) || 0;
    const convertedInputAmount = convertToBaseCurrency(currentInputAmount, currency);

    // When editing, we need to subtract the original amount from currentAmount
    // to avoid double-counting
    let adjustedCurrentAmount = currentAmount;
    if (editingMethod) {
        const originalAmount = parseMoney(editingMethod.amount) || 0;
        const convertedOriginalAmount = convertToBaseCurrency(originalAmount, editingMethod.currency, editingMethod.exchangeRate);
        adjustedCurrentAmount = currentAmount - convertedOriginalAmount;
    }

    // Add converted input to existing payments for live progress
    const totalPaidInBaseCurrency = adjustedCurrentAmount + convertedInputAmount;
    const remainingAmount = totalAmount - totalPaidInBaseCurrency;
    const progressPercentage = totalAmount > 0 ? (totalPaidInBaseCurrency / totalAmount) * 100 : 0;

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';

            if (editingMethod) {
                // Pre-populate form when editing
                setCurrency(editingMethod.currency);
                setPaymentType(editingMethod.paymentType);
                setAccountingType(editingMethod.accountingType);
                setDescription(editingMethod.description);
                setAmount(editingMethod.amount);
                setIva(editingMethod.iva);
                setPaymentDate(editingMethod.paymentDate || '');
                setFinancingType(editingMethod.financingType || '');
                setFirstInstallmentDate(editingMethod.firstInstallmentDate || '');
                setInstallments(editingMethod.installments || 1);
                setUpdateIndex(editingMethod.updateIndex || '');
                setIndexBaseDate(editingMethod.indexBaseDate || '');
                setMoraRate(editingMethod.moraRate || '');
                setMoraFrequency(editingMethod.moraFrequency || '');
            } else {
                // Always reset when opening for a new method
                resetForm();
            }
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [open, editingMethod]);

    // Verificar fecha base vs último período CAC disponible
    useEffect(() => {
        if (!indexBaseDate || !updateIndex) {
            setCacWarning(null);
            return;
        }
        cacIndexService.getLastAvailablePeriod().then((lastPeriod) => {
            if (!lastPeriod) {
                setCacWarning(null);
                return;
            }
            if (indexBaseDate > lastPeriod) {
                setCacWarning(
                    `La fecha base ${indexBaseDate} es posterior al último período CAC disponible (${lastPeriod}). ` +
                    `La venta se registrará igualmente y el ajuste se aplicará automáticamente cuando se carguen esos datos.`
                );
            } else {
                setCacWarning(null);
            }
        }).catch(() => setCacWarning(null));
    }, [indexBaseDate, updateIndex]);

    const resetForm = () => {
        setCurrency('ARS');
        setPaymentType('cash');
        setAccountingType('A');
        setDescription('');
        setAmount('');
        setIva('');
        setPaymentDate('');
        setFinancingType('');
        setFirstInstallmentDate('');
        setInstallments(1);
        setUpdateIndex('');
        setIndexBaseDate('');
        setMoraRate('');
        setMoraFrequency('');
    };

    const handleAddPayment = () => {
        // Check if adding this payment would exceed the total
        if (totalPaidInBaseCurrency > totalAmount) {
            const exceededAmount = totalPaidInBaseCurrency - totalAmount;
            showWarning(
                'Monto excedido',
                `El monto ingresado excede el total restante por ${baseCurrency} ${exceededAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}. Por favor, ajuste el monto.`
            );
            return;
        }

        const payment: PaymentMethod = {
            id: editingMethod ? editingMethod.id : Date.now().toString(),
            currency,
            paymentType,
            accountingType,
            description,
            amount,
            iva: accountingType === 'A' ? iva : '',
            exchangeRate, // Save current exchange rate with this payment
            paymentDate: paymentType === 'cash' ? paymentDate : undefined,
            financingType: paymentType === 'financing' ? financingType : undefined,
            firstInstallmentDate: paymentType === 'financing' ? firstInstallmentDate : undefined,
            installments: paymentType === 'financing' ? installments : undefined,
            updateIndex: paymentType === 'financing' && currency === 'ARS' ? updateIndex : undefined,
            indexBaseDate: paymentType === 'financing' && currency === 'ARS' ? indexBaseDate : undefined,
            moraRate: !updateIndex && moraRate ? moraRate : undefined,
            moraFrequency: !updateIndex && moraRate ? moraFrequency : undefined
        };

        if (editingMethod) {
            onUpdatePayment(payment);
        } else {
            onAddPayment(payment);
        }
        onOpenChange(false);
    };

    const formatDisplayDate = (dateString: string) => {
        if (!dateString) return 'Seleccionar fecha';
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (!open) return null;

    return createPortal(
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-black/50 z-50 transition-opacity"
                onClick={() => onOpenChange(false)}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                <div
                    className="relative w-full max-w-4xl max-h-[90vh] bg-background rounded-xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-start justify-between p-6 border-b border-border bg-background sticky top-0 z-10">
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                                <CreditCard className="w-7 h-7" />
                                Agregar Método de Pago
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Configure los detalles del método de pago
                            </p>
                        </div>
                        <button
                            onClick={() => onOpenChange(false)}
                            className="p-2 rounded-lg transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
                            aria-label="Cerrar"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="px-6 py-4 bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20 border-b border-primary/20">
                        <div className="mb-2 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-foreground" />
                                <span className="text-base font-semibold text-foreground">Progreso de Pago</span>
                            </div>
                            <span className="text-base font-bold text-primary">{progressPercentage.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-500"
                                style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                            />
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                            <div>
                                <p className="text-muted-foreground">Total</p>
                                <p className="font-bold text-foreground">{baseCurrency} {totalAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Monto Actual</p>
                                <p className="font-bold text-green-600 dark:text-green-500">{baseCurrency} {currentAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Monto Restante</p>
                                <p className="font-bold text-orange-600 dark:text-orange-500">{baseCurrency} {remainingAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
                            </div>
                        </div>
                    </div>

                    {/* Content - Scrollable */}
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="space-y-6">
                            {/* Exchange Rate */}
                            <div className="bg-muted/50 rounded-lg p-4 border border-border">
                                <div className="flex items-center gap-2 mb-3">
                                    <DollarSign className="w-5 h-5 text-primary" />
                                    <h3 className="font-semibold text-foreground">Cotización</h3>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-foreground whitespace-nowrap">1 USD =</span>
                                    <MoneyInput
                                        value={exchangeRate}
                                        onValueChange={(val) => onExchangeRateChange(val?.toString() || '')}
                                        placeholder="0.00"
                                        className="flex-1"
                                        currency="ARS"
                                    />
                                </div>
                            </div>

                            {/* Basic Configuration */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Currency */}
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                                        Moneda <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                    </Label>
                                    <Select value={currency} onValueChange={(value: 'ARS' | 'USD') => setCurrency(value)}>
                                        <SelectTrigger className="rounded-lg bg-background border-border">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="ARS">ARS (Pesos Argentinos)</SelectItem>
                                            <SelectItem value="USD">USD (Dólares)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Payment Type */}
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                                        Tipo de Pago <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                    </Label>
                                    <Select value={paymentType} onValueChange={(value: 'cash' | 'financing') => setPaymentType(value)}>
                                        <SelectTrigger className="rounded-lg bg-background border-border">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="cash">Efectivo</SelectItem>
                                            <SelectItem value="financing">Financiado</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Accounting Type */}
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                                        Contabilidad <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                    </Label>
                                    <Select value={accountingType} onValueChange={(value: 'A' | 'B') => setAccountingType(value)}>
                                        <SelectTrigger className="rounded-lg bg-background border-border">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="A">Tipo A (con IVA)</SelectItem>
                                            <SelectItem value="B">Tipo B (sin IVA)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Description */}
                                <div className="space-y-2">
                                    <Label htmlFor="payment-description" className="text-xs font-medium text-foreground sm:text-sm">
                                        Descripción
                                    </Label>
                                    <Input
                                        id="payment-description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        maxLength={255}
                                        placeholder="Ej: Anticipo, Cuota inicial..."
                                        className="rounded-lg bg-background border-border focus-visible:ring-primary"
                                    />
                                    <AtLimit value={description} max={255} />
                                </div>
                            </div>

                            {/* Amount and IVA */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                                        Monto Total ({currency}) <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                    </Label>
                                    <MoneyInput
                                        value={parseMoney(amount) ?? ''}
                                        onValueChange={(val) => setAmount(val !== null ? formatMoney(val) : '')}
                                        placeholder={(() => {
                                            const rate = parseFloat(exchangeRate) || 1;
                                            // If currency matches base, show remaining directly
                                            if (currency === baseCurrency) {
                                                return remainingAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 });
                                            }
                                            // Convert remaining from base currency to selected currency
                                            const convertedRemaining = baseCurrency === 'USD'
                                                ? remainingAmount * rate  // USD to ARS
                                                : remainingAmount / rate; // ARS to USD
                                            return convertedRemaining.toLocaleString('es-AR', { minimumFractionDigits: 2 });
                                        })()}
                                        currency={currency}
                                    />
                                    {/* Quick percentage buttons — base is fixed remaining BEFORE this input */}
                                    <div className="flex items-center gap-1.5 mt-2">
                                        <span className="text-xs text-muted-foreground shrink-0">Del total:</span>
                                        {[25, 50, 75, 100].map((pct) => {
                                            const baseRemaining = Math.max(0, totalAmount - adjustedCurrentAmount);
                                            const rate = parseFloat(exchangeRate) || 1;
                                            const baseRemainingInSelectedCurrency = currency === baseCurrency
                                                ? baseRemaining
                                                : baseCurrency === 'USD'
                                                    ? baseRemaining * rate
                                                    : baseRemaining / rate;
                                            const pctAmount = baseRemainingInSelectedCurrency * (pct / 100);
                                            return (
                                                <button
                                                    key={pct}
                                                    type="button"
                                                    onClick={() => setAmount(formatMoney(pctAmount))}
                                                    disabled={baseRemaining <= 0}
                                                    className="px-2 py-0.5 text-xs font-medium rounded-md border border-border bg-muted/50 text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    {pct}%
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {amount && currentInputAmount > 0 && currency !== baseCurrency && (() => {
                                        const targetCurrency = currency === 'USD' ? 'ARS' : 'USD';
                                        const converted = convertToBaseCurrency(currentInputAmount, currency);
                                        return (
                                            <p className="text-xs text-muted-foreground mt-1">
                                                ≈ {targetCurrency} {converted.toLocaleString('es-AR', { minimumFractionDigits: 2 })} (cotización: 1 USD = {exchangeRate || '...'} ARS)
                                            </p>
                                        );
                                    })()}
                                </div>

                                {accountingType === 'A' && (
                                    <div className="space-y-2">
                                        <Label className="text-xs font-medium text-foreground sm:text-sm">
                                            IVA <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                        </Label>
                                        <Select value={iva} onValueChange={setIva}>
                                            <SelectTrigger className="rounded-lg bg-background border-border">
                                                <SelectValue placeholder="Seleccionar..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="0">0%</SelectItem>
                                                <SelectItem value="10.5">10,5%</SelectItem>
                                                <SelectItem value="21">21%</SelectItem>
                                                <SelectItem value="27">27%</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>

                            {/* Cash Payment Fields */}
                            {paymentType === 'cash' && (
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                                        Fecha de Pago <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                    </Label>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setIsPaymentDateCalendarOpen(true)}
                                        className="w-full justify-start font-normal rounded-lg"
                                    >
                                        <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                                        <span className={paymentDate ? 'text-foreground' : 'text-muted-foreground'}>
                                            {formatDisplayDate(paymentDate)}
                                        </span>
                                    </Button>
                                </div>
                            )}

                            {/* Financing Fields */}
                            {paymentType === 'financing' && (
                                <div className="space-y-4 border-t pt-4">
                                    <h3 className="text-lg font-semibold text-foreground">Datos de Financiamiento</h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Financing Type */}
                                        <div className="space-y-2">
                                            <Label className="text-xs font-medium text-foreground sm:text-sm">
                                                Tipo de Financiación <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                            </Label>
                                            <Select value={financingType} onValueChange={setFinancingType}>
                                                <SelectTrigger className="rounded-lg bg-background border-border">
                                                    <SelectValue placeholder="Seleccionar..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="libre">Libre</SelectItem>
                                                    <SelectItem value="cuotas_mensuales">Cuotas Mensuales</SelectItem>
                                                    <SelectItem value="cuotas_bimensuales">Cuotas Bimensuales</SelectItem>
                                                    <SelectItem value="cuotas_trimestrales">Cuotas Trimestrales</SelectItem>
                                                    <SelectItem value="cuotas_cuatrimestrales">Cuotas Cuatrimestrales</SelectItem>
                                                    <SelectItem value="cuotas_pentamestrales">Cuotas Pentamestrales</SelectItem>
                                                    <SelectItem value="cuotas_semestrales">Cuotas Semestrales</SelectItem>
                                                    <SelectItem value="cuotas_anuales">Cuotas Anuales</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* Installments */}
                                        <div className="space-y-2">
                                            <Label htmlFor="installments" className="text-xs font-medium text-foreground sm:text-sm">
                                                Cantidad de Cuotas <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                            </Label>
                                            <Input
                                                id="installments"
                                                type="number"
                                                min="1"
                                                value={installments}
                                                onChange={(e) => setInstallments(parseInt(e.target.value) || 1)}
                                                placeholder="12"
                                                className="rounded-lg bg-background border-border focus-visible:ring-primary"
                                            />
                                            {amount && installments > 0 && (() => {
                                                const parsedAmount = parseMoney(amount);
                                                return parsedAmount ? (
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {installments} x {currency} {(parsedAmount / installments).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </p>
                                                ) : null;
                                            })()}
                                        </div>

                                        {/* First Installment Date */}
                                        <div className="space-y-2">
                                            <Label className="text-xs font-medium text-foreground sm:text-sm">
                                                Fecha de Primera Cuota <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                            </Label>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => setIsFirstInstallmentCalendarOpen(true)}
                                                className="w-full justify-start font-normal rounded-lg"
                                            >
                                                <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                                                <span className={firstInstallmentDate ? 'text-foreground' : 'text-muted-foreground'}>
                                                    {formatDisplayDate(firstInstallmentDate)}
                                                </span>
                                            </Button>
                                        </div>

                                        {/* Index fields only for ARS */}
                                        {currency === 'ARS' && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                                                        Índice de Actualización
                                                    </Label>
                                                    <Select value={updateIndex} onValueChange={(v) => { setUpdateIndex(v); if (v) { setMoraRate(''); setMoraFrequency(''); } }}>
                                                        <SelectTrigger className="rounded-lg bg-background border-border">
                                                            <SelectValue placeholder="Sin índice" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="CACG">CAC General</SelectItem>
                                                            <SelectItem value="CACO">CAC Obra</SelectItem>
                                                            <SelectItem value="CACM">CAC Materiales</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                {updateIndex && (
                                                    <div className="space-y-2">
                                                        <Label className="text-xs font-medium text-foreground sm:text-sm">
                                                            Fecha Base del Índice <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                                        </Label>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            onClick={() => setIsIndexBaseDateCalendarOpen(true)}
                                                            className="w-full justify-start font-normal rounded-lg"
                                                        >
                                                            <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                                                            <span className={indexBaseDate ? 'text-foreground' : 'text-muted-foreground'}>
                                                                {formatDisplayDate(indexBaseDate)}
                                                            </span>
                                                        </Button>
                                                        {cacWarning && (
                                                            <div className="flex gap-2.5 p-3 rounded-lg border border-amber-400/30 bg-amber-400/8">
                                                                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                                                <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                                                                    {cacWarning}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}

                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Mora — visible for any payment type without CAC index */}
                        {!updateIndex && (
                            <div className="space-y-4 border-t pt-4">
                                <h3 className="text-base font-semibold text-foreground">Mora por vencimiento</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="mora-rate" className="text-xs font-medium text-foreground sm:text-sm">
                                            Tasa de Mora (%)
                                        </Label>
                                        <Input
                                            id="mora-rate"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={moraRate}
                                            onChange={(e) => setMoraRate(e.target.value)}
                                            placeholder="Ej: 2.5"
                                            className="rounded-lg bg-background border-border focus-visible:ring-primary"
                                        />
                                        <p className="text-xs text-muted-foreground">Opcional. Se aplica sobre el monto impago a partir del vencimiento.</p>
                                    </div>

                                    {moraRate && parseFloat(moraRate) > 0 && (
                                        <div className="space-y-2">
                                            <Label className="text-xs font-medium text-foreground sm:text-sm">
                                                Frecuencia de Mora <span className="text-xs text-red-500">(Campo obligatorio)</span>
                                            </Label>
                                            <Select value={moraFrequency} onValueChange={setMoraFrequency}>
                                                <SelectTrigger className="rounded-lg bg-background border-border">
                                                    <SelectValue placeholder="Seleccionar frecuencia..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="DAILY">Diaria</SelectItem>
                                                    <SelectItem value="MONTHLY">Mensual</SelectItem>
                                                    <SelectItem value="ANNUAL">Anual</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 p-6 border-t border-border bg-background sticky bottom-0">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleAddPayment}
                            disabled={
                                !amount ||
                                (accountingType === 'A' && !iva) ||
                                (paymentType === 'cash' && !paymentDate) ||
                                (paymentType === 'financing' && (!financingType || !firstInstallmentDate)) ||
                                (paymentType === 'financing' && !!updateIndex && !indexBaseDate) ||
                                (!!moraRate && parseFloat(moraRate) > 0 && !moraFrequency)
                            }
                        >
                            Agregar Método de Pago
                        </Button>
                    </div>
                </div>
            </div>

            {/* Calendar Modals */}
            <CalendarModal
                open={isPaymentDateCalendarOpen}
                onOpenChange={setIsPaymentDateCalendarOpen}
                selectedDate={paymentDate ? new Date(paymentDate + 'T00:00:00') : undefined}
                onSelectDate={(date: Date) => {
                    const dateStr = date.toISOString().split('T')[0];
                    setPaymentDate(dateStr);
                    setIsPaymentDateCalendarOpen(false);
                }}
            />

            <CalendarModal
                open={isFirstInstallmentCalendarOpen}
                onOpenChange={setIsFirstInstallmentCalendarOpen}
                selectedDate={firstInstallmentDate ? new Date(firstInstallmentDate + 'T00:00:00') : undefined}
                onSelectDate={(date: Date) => {
                    const dateStr = date.toISOString().split('T')[0];
                    setFirstInstallmentDate(dateStr);
                    setIsFirstInstallmentCalendarOpen(false);
                }}
            />

            <CalendarModal
                open={isIndexBaseDateCalendarOpen}
                onOpenChange={setIsIndexBaseDateCalendarOpen}
                selectedDate={indexBaseDate ? new Date(indexBaseDate + 'T00:00:00') : undefined}
                onSelectDate={(date: Date) => {
                    const dateStr = date.toISOString().split('T')[0];
                    setIndexBaseDate(dateStr);
                    setIsIndexBaseDateCalendarOpen(false);
                }}
            />
        </>,
        document.body
    );
};

export default PaymentMethodModal;
